import { formatISO } from "date-fns";

import type { TInventoryItem } from "@up/data";
import type { Course, Graph, Organization, WebSite } from "schema-dts";

type SEO = {
  title: string;
  description: string;
  openGraph: {
    site_name: string;
    type: string;
    url: string;
    image: string;
    imageWidth: string;
    imageHeight: string;
  };
  twitter: {
    cardType: string;
  };
};

export function generateLD({
  seo,
  path,
  courseData,
  courseName,
  description,
}: {
  seo: SEO;
  path: string;
  courseData: Array<TInventoryItem>;
  courseName?: string;
  description?: string;
}) {
  const webSite: WebSite = {
    "@type": "WebSite",
    name: seo.openGraph.site_name,
    url: seo.openGraph.url,
    inLanguage: "en-US",
    publisher: {
      "@id": `${seo.openGraph.url}/#organization`,
    },
  };

  const organization: Organization = {
    "@type": "Organization",
    "@id": `${seo.openGraph.url}/#organization`,
    name: seo.openGraph.site_name,
    url: seo.openGraph.url,
    logo: {
      "@type": "ImageObject",
      url: `${seo.openGraph.url}/logo-navigation.png`,
    },
  };

  const _graph: Array<WebSite | Organization | Course> = [
    webSite,
    organization,
  ];

  if (courseName && courseData?.length) {
    const course: Course = {
      "@type": "Course",
      name: courseName,
      description,
      url: `${seo.openGraph.url}${path}`,
      provider: {
        "@type": "Organization",
        name: seo.openGraph.site_name,
      },
    };

    course.offers = [
      {
        "@type": "Offer",
        category: "Paid",
        price: courseData[0].retailPrice / 100,
        priceCurrency: "USD",
      },
    ];
    course.hasCourseInstance = courseData.map((item) => {
      return {
        "@type": "CourseInstance",
        courseMode: "online",
        courseWorkload:
          item.catalogueItem.type === "collegeCredit" ? "P128H" : "P30H",
        name: item.catalogueItem.name,
        endDate: formatISO(item.endDate, { representation: "date" }),
        startDate: formatISO(item.startDate, { representation: "date" }),
      };
    });

    _graph.push(course);
  }

  const graph: Graph["@graph"] = _graph;

  const LD: Graph = {
    "@context": "https://schema.org",
    "@graph": graph,
  };

  return LD;
}
